import { computed, inject } from '@angular/core';
import { SortOrder } from '@map/enums';
import { CreateZoneDto, Occupancy, UpdateZoneDto, Zone } from '@map/models';
import { ZoneService } from '@map/services/zone.service';
import { tapResponse } from '@ngrx/operators';
import { signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { addEntity, removeEntity, updateEntity, upsertEntities, withEntities } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { forkJoin, of, pipe, switchMap, tap } from 'rxjs';
import { updateState, withDevtools, withDevToolsStub } from '@angular-architects/ngrx-toolkit';
import { environment } from 'src/environments/environment';
import { ReportZoneData } from 'src/app/report/models';

type ZoneState = {
    selected: Zone | null;
    occupancyList: Occupancy[] | null;
    filter: {
        query: string | null;
        order: SortOrder;
    };
    loading: boolean;
    loaded: boolean;
};

const initialState: ZoneState = {
    selected: null,
    occupancyList: null,
    filter: { query: '', order: SortOrder.Ascending },
    loading: false,
    loaded: false,
};

export const withTreeShakableDevTools = environment.production ? withDevToolsStub : withDevtools;

export const ZoneStore = signalStore(
    { providedIn: 'root' },
    withTreeShakableDevTools('zones'),
    withEntities<Zone>(),
    withState<ZoneState>(initialState),
    withComputed(({ entities, filter }) => ({
        zoneCount: computed(() => entities().length),
        searchResults: computed(() => {
            return entities()
                .filter((zone) => (zone.zoneName ?? '').toLowerCase().includes(filter.query()?.toLowerCase() ?? ''))
                .sort((a, b) =>
                    filter.order() === SortOrder.Ascending ? a.zoneName.localeCompare(b.zoneName) : b.zoneName.localeCompare(a.zoneName)
                );
        }),
        enabledZones: computed(() => entities().filter((zone) => zone.zoneEnabled)),
    })),
    withMethods((store, zoneService = inject(ZoneService)) => ({
        updateFilter: (filter: { query?: string; order?: SortOrder }) => {
            updateState(store, '[Zone] Update Filter', {
                filter: {
                    ...store.filter(),
                    query: filter.query ?? '',
                    order: filter.order ?? SortOrder.Ascending,
                },
            });
        },
        resetFilter: () => updateState(store, '[Zone] Reset Filter', { filter: initialState.filter }),
        getAll: rxMethod<void>(
            pipe(
                switchMap(() => {
                    if (store.loaded()) {
                        return of(store.entities());
                    }
                    updateState(store, '[Zone] Get All', { loading: true });
                    return zoneService.getAll<Zone[]>().pipe(
                        tapResponse({
                            next: (zones) => {
                                updateState(store, '[Zone] Get All Success', upsertEntities(zones), {
                                    loaded: true,
                                    loading: false,
                                });
                            },
                            error: (error) => {
                                console.error('Failed to load zones', error);
                                updateState(store, '[Zone] Get All Failure', { loading: false });
                            },
                        })
                    );
                })
            )
        ),
        get: rxMethod<number>(
            pipe(
                switchMap((id) => {
                    updateState(store, '[Zone] Get', { loading: true });
                    return zoneService.get<Zone>(id).pipe(
                        tapResponse({
                            next: (zone) => {
                                updateState(store, '[Zone] Get Success', {
                                    selected: zone,
                                    loading: false,
                                });
                            },
                            error: (error) => {
                                console.error('Failed to load zone', error);
                                updateState(store, '[Zone] Get Failure', { loading: false });
                            },
                        })
                    );
                })
            )
        ),
        select: rxMethod<{ id: number; loadFromServer?: boolean }>(
            pipe(
                switchMap(({ id, loadFromServer }) => {
                    if (loadFromServer) {
                        updateState(store, '[Zone] Select From Server', { loading: true });
                        return zoneService.get<Zone>(id).pipe(
                            tapResponse({
                                next: (zone) => {
                                    updateState(store, '[Zone] Select Success', {
                                        selected: zone,
                                        loading: false,
                                    });
                                },
                                error: (error) => {
                                    console.error('Failed to select zone', error);
                                    updateState(store, '[Zone] Select Failure', { loading: false });
                                },
                            })
                        );
                    }
                    const zone = store.entityMap()[id] ?? null;
                    updateState(store, '[Zone] Select From Cache', { selected: zone });
                    return of(zone);
                })
            )
        ),
        deselect: () => {
            updateState(store, '[Zone] Deselect', { selected: null });
        },
        create: rxMethod<Zone>(
            pipe(
                switchMap((zone) => {
                    updateState(store, '[Zone] Create', { loading: true });
                    const createZoneDto = new CreateZoneDto(zone);
                    return zoneService.create<any>(createZoneDto).pipe(
                        tapResponse({
                            next: (response) => {
                                const newZone = new Zone(response);
                                updateState(store, '[Zone] Create Success', addEntity(newZone), {
                                    selected: newZone,
                                    loading: false,
                                });
                            },
                            error: (error) => {
                                console.error('Failed to create zone', error);
                                updateState(store, '[Zone] Create Failure', { loading: false });
                            },
                        })
                    );
                })
            )
        ),
        update: rxMethod<Zone>(
            pipe(
                switchMap((zone) => {
                    updateState(store, '[Zone] Update', { loading: true });
                    const zoneUpdate = new UpdateZoneDto(zone);
                    return zoneService.update<Zone>(zoneUpdate.zoneId, zoneUpdate).pipe(
                        tapResponse({
                            next: () => {
                                updateState(
                                    store,
                                    '[Zone] Update Success',
                                    updateEntity({
                                        id: zoneUpdate.zoneId,
                                        changes: () => zone,
                                    }),
                                    { loading: false }
                                );
                            },
                            error: (error) => {
                                console.error('Failed to update zone', error);
                                updateState(store, '[Zone] Update Failure', { loading: false });
                            },
                        })
                    );
                })
            )
        ),
        delete: rxMethod<number>(
            pipe(
                switchMap((id) => {
                    updateState(store, '[Zone] Delete', { loading: true });
                    return zoneService.delete(id).pipe(
                        tapResponse({
                            next: () => {
                                updateState(store, '[Zone] Delete Success', removeEntity(id), {
                                    selected: store.selected()?.id === id ? null : store.selected(),
                                    loading: false,
                                });
                            },
                            error: (error) => {
                                console.error('Failed to delete zone', error);
                                updateState(store, '[Zone] Delete Failure', { loading: false });
                            },
                        })
                    );
                })
            )
        ),
        getOccupancyFromReportData: rxMethod<{ zoneData: ReportZoneData[] }>(
            pipe(
                switchMap(({ zoneData }) => {
                    if (!zoneData || zoneData.length === 0) return of([]);
                    updateState(store, '[Zone] Get Occupancy From Report Data', { loading: true });
                    const requests = zoneData.map((data) =>
                        zoneService.getOccupancyRange({
                            id: data.zoneId,
                            from: data.dateRange[0],
                            to: data.dateRange[1],
                            daily: false,
                        })
                    );

                    return forkJoin(requests).pipe(
                        tapResponse({
                            next: (results) => {
                                const allOccupancy = results.flat();
                                updateState(store, '[Zone] Get Occupancy From Report Data Success', {
                                    occupancyList: allOccupancy,
                                    loading: false,
                                });
                            },
                            error: (error) => {
                                console.error('Failed to get occupancy from report data', error);
                                updateState(store, '[Zone] Get Occupancy From Report Data Failure', { loading: false });
                            },
                        })
                    );
                })
            )
        ),
    }))
);
